// «Saltar a…» (rebrand 03-11, como Spotlight; maqueta web/qa/rebrand-apple/despues/5-saltar.png). Pau cambia de agente en
// el 64 % de sus mensajes seguidos (analisis/uso-oficina): Ctrl/⌘+K abre un buscador; escribes tres letras y Intro. Sin
// escribir nada, primero las subidas que esperan tu Allow (Entrega 3, flujo.md #1: es lo que más tiempo para a un
// agente) y después los agentes en el orden de la lista (el que más lleva esperándote delante), así que Ctrl+K, Intro =
// «lo siguiente que me necesita». También acciones: contratar (con lo escrito como encargo) y abrir la Sala de Control.
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Activity, Group, Plus, Rocket, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import type { ContratadoDTO, GrupoDTO } from '../compartido/vocabulario';
import type { Despliegue } from '../despliegues/api';
import { useBandejaStore, useSubidas } from '../despliegues/store';
import { agenteDeSubida, subidasEnOrden } from '../despliegues/subidas';
import { useOficinaStore } from '../store';
import { IconoEncargo } from '../ui';
import { cerrarHojas, MARCA_CAPA, useEscape } from '../ui/useEscape';
import { quien, quienConFrase } from './estado';
import { aspectoDe, etiquetaSituacion, seVa } from './situacion';
import { useEquipo } from './useEquipo';

/** Otra pieza (el botón de la lista) pide abrirlo con este evento. */
export const EVENTO_SALTAR = 'oficina:saltar';
export const abrirSaltar = () => window.dispatchEvent(new Event(EVENTO_SALTAR));

/** Sin mayúsculas ni tildes: «publi» encuentra «Publicar», «catalan» encuentra «catalán». */
export function normalizar(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

type Opcion =
  | { tipo: 'subida'; d: Despliegue; de: ContratadoDTO | undefined }
  | { tipo: 'grupo'; g: GrupoDTO; miembros: ContratadoDTO[]; primero: ContratadoDTO }
  | { tipo: 'agente'; c: ContratadoDTO }
  | { tipo: 'contratar' }
  | { tipo: 'sala' };

/** Cuántas subidas se ofrecen como mucho (las demás, en la lista). */
const MAX_SUBIDAS = 5;

export function Saltar({ contratados, alAbrir, alSala }: { contratados: Record<string, ContratadoDTO>; alAbrir: (agentId: string) => void; alSala: () => void }) {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [sel, setSel] = useState(0);
  const { orden, necesita } = useEquipo(contratados);
  const { pendientes } = useSubidas();
  const abrirSubida = useBandejaStore((s) => s.abrirSubida);
  const chatAbierto = useOficinaStore((s) => s.chatAbierto);
  const abrirContratar = useOficinaStore((s) => s.abrirContratar);
  const grupos = useOficinaStore((s) => s.grupos);

  // Lo que tenía el foco al abrir (la caja del chat, el visor…): vuelve ahí al cerrar sin elegir. Radix solo lo devuelve
  // a su Dialog.Trigger, y Saltar se abre con Ctrl+K o desde el buscador de la lista, sin él: el foco caía en <body>.
  const antes = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const abrir = () => {
      if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body) antes.current = document.activeElement;
      else antes.current = null;
      setTexto('');
      setSel(0);
      setAbierto(true);
    };
    const tecla = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        abrir();
      }
    };
    window.addEventListener('keydown', tecla);
    window.addEventListener(EVENTO_SALTAR, abrir);
    return () => {
      window.removeEventListener('keydown', tecla);
      window.removeEventListener(EVENTO_SALTAR, abrir);
    };
  }, []);

  const opciones = useMemo<Opcion[]>(() => {
    const q = normalizar(texto.trim());
    const vivos = orden.filter((c) => !seVa(c));
    // Sin escribir, el que ya tienes abierto no se ofrece: Ctrl+K, Intro = «el siguiente que me necesita», no el mismo.
    // 04-11 (mesas de equipo, grupos.md §4): «precios» encuentra también a los del grupo Precios, y el grupo mismo (abre
    // el primero de él que te necesita, o el primero sin más).
    const nombreGrupo = (c: ContratadoDTO) => (c.grupoId ? (grupos.find((g) => g.id === c.grupoId)?.nombre ?? '') : '');
    const agentes = q ? vivos.filter((c) => normalizar(`${quienConFrase(c)} ${c.nombre} ${nombreGrupo(c)}`).includes(q)) : vivos.filter((c) => c.agentId !== chatAbierto);
    const deGrupos = q
      ? grupos.flatMap((g) => {
          if (!normalizar(g.nombre).includes(q)) return [];
          const miembros = vivos.filter((c) => c.grupoId === g.id);
          const primero = miembros.find(necesita) ?? miembros[0];
          return primero ? [{ tipo: 'grupo' as const, g, miembros, primero }] : [];
        })
      : [];
    const subidas = subidasEnOrden(pendientes)
      .map((d) => {
        const id = agenteDeSubida(d);
        return { tipo: 'subida' as const, d, de: id ? contratados[id] : undefined };
      })
      .filter((o) => !q || normalizar(`${o.d.paraPau.titulo} ${o.de ? quien(o.de) : ''}`).includes(q));
    return [...subidas.slice(0, MAX_SUBIDAS), ...deGrupos, ...agentes.slice(0, 8).map((c) => ({ tipo: 'agente' as const, c })), { tipo: 'contratar' }, { tipo: 'sala' }];
  }, [orden, texto, chatAbierto, pendientes, contratados, grupos, necesita]);

  // Escape va por la pila de la oficina (ui/useEscape.ts): Saltar es una hoja, encima de todo lo que estaba abierto.
  useEscape(abierto, () => setAbierto(false), { id: 'saltar' });

  // Se eligió algo: el foco lo pone lo que se abre (la caja del chat, la ventana de la subida, Contratar), no lo de antes.
  const eligio = useRef(false);
  const elegir = (o: Opcion | undefined) => {
    if (!o) return;
    eligio.current = true;
    setAbierto(false);
    // 05-11 (QA G4): elegir es ir a otro sitio: lo que estaba abierto encima de la oficina (la vista previa de un PDF, la
    // ventana de una subida, cualquier hoja) se cierra, para que lo elegido se vea y se escriba donde se ve.
    cerrarHojas('saltar');
    if (o.tipo === 'agente') alAbrir(o.c.agentId);
    else if (o.tipo === 'grupo') alAbrir(o.primero.agentId);
    else if (o.tipo === 'subida') abrirSubida(o.d.runId);
    else if (o.tipo === 'contratar') abrirContratar(undefined, texto.trim() || undefined);
    else alSala();
  };

  return (
    <DialogPrimitive.Root open={abierto} onOpenChange={setAbierto}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/40 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-label={t('oficinaV2.saltar.titulo')}
          {...{ [MARCA_CAPA]: '' }}
          // Si se eligió algo, al cerrarse no devuelve el foco a lo de antes: lo pone lo que se abre (la caja del chat, la
          // ventana de la subida). Si no (Esc, pulsar fuera), vuelve a donde estaba (QA M1). El campo lo enfoca Radix.
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            if (!eligio.current && antes.current?.isConnected) antes.current.focus({ preventScroll: true });
            eligio.current = false;
            antes.current = null;
          }}
          className="fixed left-1/2 top-[14vh] z-[61] flex max-h-[70vh] w-[min(640px,calc(100vw-2rem))] -translate-x-1/2 flex-col overflow-hidden rounded-o-xl bg-oficina-elevada text-oficina-texto shadow-o-hoja outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <DialogPrimitive.Title className="sr-only">{t('oficinaV2.saltar.titulo')}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">{t('oficinaV2.saltar.ayuda')}</DialogPrimitive.Description>
          <label className="flex items-center gap-3 border-b border-oficina-linea px-4 py-3.5">
            <Search className="h-5 w-5 shrink-0 text-oficina-texto2" aria-hidden />
            <input
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                setSel(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSel((s) => Math.min(s + 1, opciones.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSel((s) => Math.max(s - 1, 0));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  elegir(opciones[sel]);
                }
              }}
              placeholder={t('oficinaV2.saltar.placeholder')}
              aria-label={t('oficinaV2.saltar.placeholder')}
              aria-activedescendant={`saltar-${sel}`}
              aria-controls="saltar-lista"
              className="min-w-0 flex-1 bg-transparent text-o-titulo3 text-oficina-texto outline-none placeholder:text-oficina-texto3"
            />
            <kbd className="rounded-o-s border border-oficina-linea px-1.5 font-sans text-o-nota text-oficina-texto3 [@media(pointer:coarse)]:hidden">{t('oficinaV2.saltar.esc')}</kbd>
          </label>
          <ul id="saltar-lista" role="listbox" aria-label={t('oficinaV2.saltar.titulo')} className="min-h-0 overflow-y-auto p-2">
            {opciones.map((o, i) => (
              <li
                key={o.tipo === 'agente' ? o.c.agentId : o.tipo === 'subida' ? `subida-${o.d.runId}` : o.tipo === 'grupo' ? `grupo-${o.g.id}` : o.tipo}
                id={`saltar-${i}`}
                role="option"
                aria-selected={i === sel}
                onMouseMove={() => setSel(i)}
                onClick={() => elegir(o)}
                className={cn('flex cursor-pointer items-center gap-3 rounded-o-m px-2.5 py-2', i === sel && 'bg-oficina-pulsado')}
                data-testid="saltar-opcion"
              >
                {o.tipo === 'agente' ? <FilaAgente c={o.c} toca={necesita(o.c)} /> : o.tipo === 'subida' ? <FilaSubida d={o.d} de={o.de} /> : o.tipo === 'grupo' ? (
                  <FilaGrupo g={o.g} n={o.miembros.length} te={o.miembros.filter(necesita).length} />
                ) : o.tipo === 'contratar' ? (
                  <>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-o-s bg-oficina-control text-oficina-acento" aria-hidden>
                      <Plus className="h-4 w-4" strokeWidth={2.5} />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-o-texto font-medium">{texto.trim() ? t('oficinaV2.saltar.contratarPara', { texto: texto.trim() }) : t('oficinaV2.saltar.contratar')}</span>
                  </>
                ) : (
                  <>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-o-s bg-oficina-error text-white" aria-hidden>
                      <Activity className="h-4 w-4" strokeWidth={2.4} />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-o-texto font-medium">{t('oficinaV2.saltar.sala')}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
          {/* Las teclas solo sirven con teclado: con el dedo no se enseñan. */}
          <p className="flex gap-4 border-t border-oficina-linea px-4 py-2 text-o-nota text-oficina-texto3 [@media(pointer:coarse)]:hidden">{t('oficinaV2.saltar.ayuda')}</p>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function FilaSubida({ d, de }: { d: Despliegue; de: ContratadoDTO | undefined }) {
  return (
    <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-oficina-acento text-oficina-acento-tinta" aria-hidden>
        <Rocket className="h-4 w-4" strokeWidth={2.4} />
      </span>
      <span className="min-w-0 flex-1 truncate text-o-texto font-semibold">{d.paraPau.titulo}</span>
      <span className="max-w-48 shrink-0 truncate text-o-sub text-oficina-acento">{de ? quien(de) : t('oficinaV2.lista.subida.etiqueta')}</span>
    </>
  );
}

/** Un grupo de mesas: su nombre y cuántos tiene (y cuántos te esperan). Elegirlo abre el primero que te necesita. */
function FilaGrupo({ g, n, te }: { g: GrupoDTO; n: number; te: number }) {
  return (
    <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-o-s bg-oficina-control text-oficina-texto2" aria-hidden>
        <Group className="h-4 w-4" strokeWidth={2.2} />
      </span>
      <span className="min-w-0 flex-1 truncate text-o-texto font-semibold">{g.nombre}</span>
      <span className="shrink-0 text-o-sub text-oficina-texto3">{te ? t('oficinaV2.grupos.saltarTe', { n, te }) : t('oficinaV2.grupos.saltarN', { n })}</span>
    </>
  );
}

function FilaAgente({ c, toca }: { c: ContratadoDTO; toca: boolean }) {
  return (
    <>
      <IconoEncargo icono={c.icono} semilla={c.semillaAvatar} tamano={32} />
      <span className={cn('min-w-0 flex-1 truncate text-o-texto', toca ? 'font-semibold' : 'font-medium')}>{quien(c)}</span>
      <span className={cn('shrink-0 text-o-sub', toca ? aspectoDe(c).texto : 'text-oficina-texto3')}>{etiquetaSituacion(c.situacion)}</span>
    </>
  );
}
